import React from 'react';
import { Card } from '@mts/ui';
import { Pencil } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../../../components/ui/carousel';

function groupPhotos(photos, groupSize = 3) {
  const groups = [];
  for (let index = 0; index < photos.length; index += groupSize) {
    groups.push(photos.slice(index, index + groupSize));
  }
  return groups;
}

function BasicPhotosCard({ photos, isBasicTier = false, canEdit = false, onEdit }) {
  const hasPhotos = Array.isArray(photos) && photos.length > 0;
  const photoGroups = hasPhotos ? groupPhotos(photos, 3) : [];

  return (
    <div id="PhotosCard">
      <Card
        id="PhotosCard-Card"
        className="my-[10px] rounded-lg"
        style={{
          backgroundColor: 'var(--card)',
          borderColor: 'var(--border)',
          borderWidth: '1px',
          borderStyle: 'solid',
        }}
      >
      <Card.Header id="PhotosCard-CardHeader" className="flex items-start justify-between gap-3">
        <div id="PhotosCard-CardHeaderInner">
          <h2 className="text-base font-semibold text-[var(--primary)]">Photos</h2>
          <Card.Title id="PhotosCard-CardTitle">
            <span className="sr-only">Photo gallery</span>
          </Card.Title>
          <Card.Description id="PhotosCard-CardDescription">
            <span className="sr-only">Listing photos</span>
          </Card.Description>
        </div>
        <Card.Action id="PhotosCard-CardAction">
          {canEdit ? (
            <button
              type="button"
              aria-label="Edit Photos"
              title="Edit Photos"
              onClick={onEdit}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--border)] text-[var(--primary)] transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30"
            >
              <Pencil size={16} aria-hidden="true" />
            </button>
          ) : (
            <div />
          )}
        </Card.Action>
      </Card.Header>

      <Card.Content id="PhotosCard-CardContent" className="before:content-none after:content-none">
        {hasPhotos ? (
          <Carousel opts={{ align: 'start', loop: photoGroups.length > 1 }} className="relative w-full">
            <CarouselContent>
              {photoGroups.map((group, groupIndex) => (
                <CarouselItem key={`photo-group-${groupIndex}`}>
                  <div className="grid grid-cols-3 gap-2">
                    {group.map((photo) => (
                      <figure key={photo.id} className="space-y-2">
                        <div
                          className="aspect-[4/3] w-full overflow-hidden rounded-lg"
                          style={{
                            backgroundColor: 'var(--surface)',
                            filter: isBasicTier ? 'grayscale(1)' : undefined,
                          }}
                        >
                          <img
                            src={photo.url}
                            alt={photo.caption || 'Listing photo'}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      </figure>
                    ))}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            {photoGroups.length > 1 ? (
              <>
                <CarouselPrevious />
                <CarouselNext />
              </>
            ) : null}
          </Carousel>
        ) : (
          <div className="aspect-[4/3] rounded-lg" style={{ backgroundColor: 'var(--surface)' }} />
        )}
      </Card.Content>

      <Card.Footer id="PhotosCard-CardFooter">
        <div />
      </Card.Footer>
      </Card>
    </div>
  );
}

export default BasicPhotosCard;
